import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, tap } from 'rxjs/operators';

import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

// Ancienne version de SuggestableInputComponent, maintenant une ancienne compatibilite avec les Promise des requetes web AngularJS
@Component({
	selector: 'suggestable-input-js',
	templateUrl: 'suggestable-input-js.html',
	styleUrls: ['suggestable-input-js.css']
})
export class SuggestableInputJSComponent {
	// Le composant de saisie, vu comme un FormControl, pour manipulation de son contenu
	inputControl = new FormControl();

	// Fonction simplifiant l'utilisation du labelGetter dans l'interface
	getLabel: (item: any) => string = (item) => item ? this.labelGetter(item) : '';

	// Le nombre de suggestions possibles
	suggestionsCount: number = 0;

	// La liste actuelle des elements sugeres
	suggestions: string[] = [];
	
	// Indicateur de chargement des suggestions
	isLoading: boolean = false;
	
	// Le message d'erreur quand on ne peut pas recuperer la liste de suggestions
	errorMsg: string|undefined;

	// La valeur actuellement saisie dans le champ, en biding reactif pour pouvoir reagir aux changements venant du parent
	private _value: string = '';
	@Input() set value(value: string) {
		this._value = value;
		
		if (this.inputControl.value !==  value) {
			this.inputControl.setValue(value);
		}
	}
	get value(): string {
		return this._value;
	}

	// L'evenement necessaire au two-way binding pour la valeur
	@Output() valueChange = new EventEmitter<any>();

	// Evenement supplementaire pour permettre au parent de reagir apres le changement de l'element selectionne
	@Output() change = new EventEmitter<any>();
	
	constructor() {
		// Met en place la liaison de donnee dans la valeur saisie et les suggestions
		this.bindList();
	}

	// Le composant de saisie, vu comme un trigger pour la manipulation de la dropdown list
	@ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger }) input!: MatAutocompleteTrigger;

	// La class de style a appliquer au composant de saisie
	@Input() inputClass: string = '';

	@Input() maxlength: string | number | null = 200;

	// L'indicateur de desactivation du composant
	@Input() disabled: boolean = false;

	// Classe(s) a ajouter au panneau de suggestions
	@Input() panelClass: string | string[] = [];
	
	// Fonction retournant une promesse de reponse pour les suggestions en fonction d'une saisie
	@Input() suggestionsPromiseGetter!: (input: string) => Promise<any>;
	
	// La fonction a utiliser pour obtenir le libelle d'un item (si pas fourni on estime que les items suggeres sont deja des strings)
	@Input() labelGetter: (item: any) => string = (item: string) => item;

	/////////////////////////////////////
	// Gestion des actions utilisateur //
	///////////////////////////////////// 

	onInputFocus() {
		// Lorsque le focus arrive sur le composant, on reinitialise la liste filtree.
		// En effet, le contenu de la liste source peut avoir change meme si sa reference est identique
		
		// Recuperer la valeur du champ de saisie
		let value = this.inputControl.value;

		// Updater les suggestions
		this.updateSuggestions(value);
	}

	//////////////////////
	// Fonctions outils //
	//////////////////////

	// (Re-)cree une relation automatique entre la valeur de la zone de saisie et les suggestions
	private bindList() {
		this.inputControl.valueChanges
			.pipe(
				// Ignore les saisies qui ont lieu a moins de 500ms d'intervale
				debounceTime(500),
				// Initialize les valeurs de controle
				tap(() => {
					this.isLoading = true;
					this.errorMsg = undefined;
					this.suggestionsCount = 0;
					this.suggestions = [];
				})
			)
			// A la reception d'une nouvelle valeur
			.subscribe(value => {
				// Notifier le parent du changement si necessaire
				if (this._value !==  value) {
					this._value = value;
					this.valueChange.emit(value);
					this.change.emit(value);
				}
				
				// Mettre a jour les suggestions
				this.updateSuggestions(value);	
			});
	}
	
	private updateSuggestions(value: string) {
		if (!!value) {
			// Si la valeur n'est pas nulle, appeler le service
			let that = this;
			this.suggestionsPromiseGetter(value).then(
				function(resp) { // success
					if (resp.status != 200) {
						that.errorMsg = resp.data;
					} else {
						that.suggestionsCount = resp.data.possibles;
						let suggestedObjs = resp.data.suggestions;
						
						// Conversion des objets suggeres en suggestions sous forme de string, via getLabel
						// NB: Ce n'est pas obligatoire, mais cela simplifie enormement de traitements par la suite
						let suggestions = [];
						for (let i = 0; i < suggestedObjs.length; i++) {
							let suggestedObj = suggestedObjs[i];
							let label = that.getLabel(suggestedObj);
							suggestions.push(label);
						}
						
						// Affectation des suggestions
						that.suggestions = suggestions;
					}
					that.isLoading = false;
					
				},
				function(errResp) {
					that.errorMsg = errResp.data;
					that.isLoading = false;
				}
			);
		} else {
			this.isLoading = false;
			this.errorMsg = undefined;
			this.suggestionsCount = 0;
			this.suggestions = [];
		}
	}
}